import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, BookOpen } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { BUSINESS } from "@/lib/api";
import useSeo from "@/lib/useSeo";

const SECTIONS = [
  {
    h: "1. What is agricultural drone spraying?",
    p: [
      "Agricultural drone spraying — sometimes called \"drone crop dusting\" — is the use of FAA-licensed multirotor or hybrid drones to apply pesticides, herbicides, fungicides, foliar fertilizers, biologicals or seed to fields, groves and nurseries. In Florida, every commercial drone application requires both an FAA Part 107 remote-pilot license and a Part 137 agricultural operator certificate, plus a state applicator license from FDACS.",
      "Compared to traditional ground rigs or manned crop dusters, drones offer a unique combination: lower per-acre cost than a Cessna, zero soil compaction, terrain-following flight that handles rocky Redland soils and tropical-fruit canopies, and pinpoint GPS swath control that cuts chemical use 20–30%.",
    ],
  },
  {
    h: "2. Why drone spraying is winning in South Florida",
    p: [
      "South Florida is a unique agricultural zone — limestone soils that don't take tractor traffic, dense avocado and mango canopies, narrow nursery rows in the Redland, irrigated ditches and pollinator-sensitive borders. All of these are environments where conventional ground sprayers either can't go, get stuck, or waste chemical.",
      "Drones can fly the same field three weeks in a row without leaving a single tire track. They penetrate dense canopies top-down with calibrated downwash. They follow GPS-loaded prescription maps so two fungicide passes don't double-overlap. And they mobilize in hours, not days — critical when a fungal outbreak hits during a wet week in Homestead or Florida City.",
    ],
  },
  {
    h: "3. Services covered: pesticide, herbicide, fungicide, fertilizer, seeding",
    p: [
      "AeroAcres applies EPA-labeled insecticides (thrips, whitefly, mites, armyworm), herbicides (band-spray between rows, ditch-bank, fence-line), fungicides (anthracnose, powdery mildew, rust, laurel-wilt support programs), foliar fertilizers and micronutrients, biologicals, and cover-crop seed via aerial broadcast.",
      "We source the chemical or apply yours — either way, every job carries a chain-of-custody record. Most growers run programs that tank-mix multiple chemistries in a single flight to save trips.",
    ],
  },
  {
    h: "4. How much does drone spraying cost in Florida?",
    p: [
      "Per-acre pricing varies by chemical, crop and access. Across the Homestead and Miami-Dade region, typical AeroAcres rates are: pesticide $15–$25/acre, herbicide $14–$22/acre, fungicide $18–$26/acre, foliar fertilizer $13–$20/acre, cover-crop seeding $10–$15/acre.",
      "Volume discounts apply above 50 acres. Same-day rush jobs carry a modest uplift to cover crew scheduling. Most jobs come in well below the cost of a manned aerial applicator and are competitive with hiring a ground crew on overtime.",
    ],
  },
  {
    h: "5. FAA Part 137, licensing and what to ask any applicator",
    p: [
      "Before hiring any drone applicator in Florida, confirm three credentials: (1) FAA Part 107 remote pilot license, (2) FAA Part 137 agricultural operator certificate held by the company, (3) a Florida FDACS commercial applicator license. Without all three, the application is not legal — and crop-insurance audits will reject it.",
      "Ask for a sample coverage report. A real applicator will provide a flight log, GPS swath map and applied-rate map for every job. If they can't, the application probably wasn't done to spec.",
    ],
  },
  {
    h: "6. Drone vs. ground rig vs. crop duster — when each wins",
    p: [
      "Ground rigs make sense on perfectly flat, dry, large fields with low canopy and easy access — sugarcane on the Glades edge, for example. Manned crop dusters make sense at scale (500+ acres) on uniform commercial farms where the per-flight setup cost amortizes across thousands of acres.",
      "Drones win everywhere in between: 5–250 acre fields, tight buffers, dense canopies, mixed crops, organic programs, pollinator-sensitive borders, and any time turnaround matters more than raw throughput. That covers about 90% of South Florida agriculture.",
    ],
  },
  {
    h: "7. What growers should provide for a drone spray",
    p: [
      "To prep for a drone spray job, growers should have: a clear field boundary (a Google Earth pin or KML is ideal), notes on access (gate codes, no-fly buffers, beehives, neighbouring crops), the target pest/disease, any preferred chemical or active ingredient, and a clean water source within a reasonable distance of the launch point.",
      "AeroAcres handles everything else — pilot scheduling, FAA paperwork, chemical sourcing if needed, and the post-flight coverage report.",
    ],
  },
];

export default function UltimateGuide() {
  useSeo({
    title: "Drone Crop Spraying in South Florida — The Complete Guide | AeroAcres",
    description:
      "The complete grower's guide to agricultural drone spraying in South Florida — services, pricing, FAA licensing, comparison to ground rigs and crop dusters. Free quote from AeroAcres.",
    path: "/drone-spraying-florida-guide/",
  });

  return (
    <div className="min-h-screen bg-background" data-testid="seo-guide-page">
      <Header />

      <section className="pt-[120px] pb-12 bg-muted/40 border-b border-border">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-accent/10 text-accent px-3.5 py-1.5 text-xs font-semibold tracking-wide">
            <BookOpen className="h-3.5 w-3.5" /> The complete guide
          </div>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-primary tracking-tight">
            Drone Crop Spraying in South Florida
          </h1>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            Everything growers in Homestead, Miami-Dade, Broward and the Redland need to know about choosing, hiring and using an
            agricultural drone spray service in 2026. Updated by AeroAcres' working FAA Part 137 pilots.
          </p>
        </div>
      </section>

      <article className="py-16 md:py-20 bg-background">
        <div className="max-w-3xl mx-auto px-5 md:px-8 space-y-12">
          {SECTIONS.map((s, i) => (
            <motion.section
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="font-display text-2xl sm:text-3xl text-primary font-bold leading-tight">{s.h}</h2>
              {s.p.map((p, j) => (
                <p key={j} className="mt-4 text-foreground/85 leading-relaxed">{p}</p>
              ))}
            </motion.section>
          ))}
        </div>
      </article>

      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
            Ready to put this into practice on your fields?
          </h2>
          <p className="mt-4 text-primary-foreground/80">Get a free site-specific quote in under 24 hours.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="rounded-full bg-accent hover:bg-accent/90 h-12 px-7 text-base font-semibold">
              <Link to="/#contact">Get a quote <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full bg-transparent border-white/40 text-white hover:bg-white/10 h-12 px-7 text-base font-semibold">
              <a href={`tel:${BUSINESS.phoneRaw}`} data-conversion="phone-call" className="track-phone-click">
                <Phone className="h-4 w-4 mr-1.5" /> Call {BUSINESS.phone}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
